/**
 * Messages between the reader page (part 39) and its layout worker, and the parsers that check them on arrival in each
 * direction (Definition of Done, security: nothing unvalidated crosses a boundary, as the preview's protocol, part 12).
 * The page opens a reading copy's book on a device profile, and the worker lays its chapters out one after another,
 * saying how many pages each has as it goes; the page asks for pages by their place in the book, and the worker draws
 * them and sends each with its text layer (part 39 log, RP3) and what it reads as. Every request carries an ID its
 * answers repeat.
 */
import { FONT_STYLES } from '../font/faces.ts';
import type { FaceKey } from '../font/faces.ts';
import type { LayerBlock, LayerRun } from '../pages/layer.ts';
import { FONT_IDS } from '../theme/fonts.ts';
import { DEVICE_PROFILES } from '../theme/profiles.ts';
import type { ProfileId } from '../theme/profiles.ts';
import { MAX_CHAPTER_TEXT, MAX_MESSAGE_LENGTH, MAX_SCALE, MIN_SCALE, ProtocolError } from '../worker/protocol.ts';
import type { Bitmap } from '../worker/protocol.ts';

/** A chapter of the book to lay out: its title as the preview shows it, and its text. */
export interface ReaderChapter {
  readonly title: string;
  readonly text: string;
}

/** An image of the book: its ID and hash, as the text names it, and its bytes (checked against the hash in the worker). */
export interface ReaderImage {
  readonly id: string;
  readonly sha256: string;
  readonly bytes: Uint8Array<ArrayBuffer>;
}

/** Page → worker. The theme and metadata are part 05's serialised records, read in the worker. */
export type ReaderRequest =
  | {
      readonly id: number;
      readonly kind: 'open';
      readonly profile: ProfileId;
      readonly theme: string;
      readonly metadata: string;
      readonly chapters: readonly ReaderChapter[];
      readonly images: readonly ReaderImage[];
    }
  | { readonly id: number; readonly kind: 'draw'; readonly pages: readonly number[]; readonly scale: number };

/** A page drawn: its place in the book (from 0), its chapter (from 0), its pixels, its text layer and what it reads as. */
export interface DrawnPage<B extends object = Bitmap> {
  readonly index: number;
  readonly chapter: number;
  readonly bitmap: B;
  /** Where its text area starts on the page, points from its top left (the layer's runs are placed from there). */
  readonly origin: { readonly x: number; readonly y: number };
  readonly layer: readonly LayerBlock[];
  readonly reading: string;
}

/** Worker → page. `laid` says each chapter's pages so far (complete: every chapter laid out); `stale`: a newer open came. */
export type ReaderResponse<B extends object = Bitmap> =
  | { readonly id: number; readonly kind: 'laid'; readonly chapters: readonly number[]; readonly complete: boolean }
  | { readonly id: number; readonly kind: 'drawn'; readonly pages: readonly DrawnPage<B>[] }
  | { readonly id: number; readonly kind: 'stale' }
  | { readonly id: number; readonly kind: 'error'; readonly message: string };

/** Most chapters a book opens with: the reading copy's own bound (core's `MAX_CHAPTERS`, kept here so the main entry reaches no package). */
export const MAX_READER_CHAPTERS = 1_000;
/** Most images it carries, and the most bytes of them in all: a copy's 60 MiB (part 38's 30 parts of 2 MiB). */
export const MAX_READER_IMAGES = 2_000;
export const MAX_READER_IMAGE_BYTES = 60 * 1024 * 1024;
/** Longest serialised theme or metadata record: far beyond any written. */
export const MAX_RECORD_TEXT = 100_000;
/** Most pages one draw asks for: a spread and the pages either side. */
export const MAX_DRAW_PAGES = 8;
/** Most pages a book may have, and most runs, blocks and units of text a page's layer may hold. */
export const MAX_BOOK_PAGES = 1_000_000;
export const MAX_LAYER_RUNS = 20_000;
export const MAX_LAYER_TEXT = 200_000;

const PROFILE_IDS: readonly ProfileId[] = DEVICE_PROFILES.map((profile) => profile.id);
const ID = /^[0-9a-f]{32}$/u;
const HASH = /^[0-9a-f]{64}$/u;

/** The own fields of `value` when it is a plain object of exactly `keys`; throws otherwise. */
function fieldsOf(value: unknown, keys: readonly string[], what: string): ReadonlyMap<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new ProtocolError(`${what} must be an object`);
  const fields = new Map(Object.entries(value));
  if (fields.size !== keys.length || !keys.every((key) => fields.has(key))) throw new ProtocolError(`${what} must have exactly ${keys.join(', ')}`);
  return fields;
}

function idOf(fields: ReadonlyMap<string, unknown>): number {
  const id = fields.get('id');
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id < 0) throw new ProtocolError('a message id must be a whole number');
  return id;
}

function text(value: unknown, max: number, what: string): string {
  if (typeof value !== 'string' || value.length > max) throw new ProtocolError(`${what} must be text of at most ${String(max)} units`);
  return value;
}

function whole(value: unknown, min: number, max: number, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max) throw new ProtocolError(`${what} must be a whole number from ${String(min)} to ${String(max)}`);
  return value;
}

function finite(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ProtocolError(`${what} must be a finite number`);
  return value;
}

function list(value: unknown, max: number, what: string): readonly unknown[] {
  if (!Array.isArray(value) || value.length > max) throw new ProtocolError(`${what} must be a list of at most ${String(max)}`);
  return value;
}

/** A message the page sent, checked; throws {@link ProtocolError} for anything else. */
export function parseReaderRequest(value: unknown): ReaderRequest {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'draw') {
    const fields = fieldsOf(value, ['id', 'kind', 'pages', 'scale'], 'a draw');
    const pages = list(fields.get('pages'), MAX_DRAW_PAGES, 'pages').map((page) => whole(page, 0, MAX_BOOK_PAGES, 'a page'));
    const scale = finite(fields.get('scale'), 'scale');
    if (scale < MIN_SCALE || scale > MAX_SCALE) throw new ProtocolError(`scale must be from ${String(MIN_SCALE)} to ${String(MAX_SCALE)}`);
    return { id: idOf(fields), kind, pages, scale };
  }
  if (kind !== 'open') throw new ProtocolError('a request must be an open or a draw');
  const fields = fieldsOf(value, ['id', 'kind', 'profile', 'theme', 'metadata', 'chapters', 'images'], 'an open');
  const profile = PROFILE_IDS.find((known) => known === fields.get('profile'));
  if (profile === undefined) throw new ProtocolError('profile must be a device profile');
  const chapters = list(fields.get('chapters'), MAX_READER_CHAPTERS, 'chapters').map((raw) => {
    const chapter = fieldsOf(raw, ['title', 'text'], 'a chapter');
    return { title: text(chapter.get('title'), MAX_RECORD_TEXT, 'a title'), text: text(chapter.get('text'), MAX_CHAPTER_TEXT, 'a chapter’s text') };
  });
  let bytes = 0;
  const images = list(fields.get('images'), MAX_READER_IMAGES, 'images').map((raw) => {
    const image = fieldsOf(raw, ['id', 'sha256', 'bytes'], 'an image');
    const id = image.get('id');
    const sha256 = image.get('sha256');
    const data = image.get('bytes');
    if (typeof id !== 'string' || !ID.test(id) || typeof sha256 !== 'string' || !HASH.test(sha256)) throw new ProtocolError('an image needs its ID and hash');
    if (!(data instanceof Uint8Array) || !(data.buffer instanceof ArrayBuffer)) throw new ProtocolError('an image’s bytes must be bytes');
    bytes += data.length;
    if (bytes > MAX_READER_IMAGE_BYTES) throw new ProtocolError('the images must be at most 60 MiB in all');
    return { id, sha256, bytes: new Uint8Array(data.buffer, data.byteOffset, data.byteLength) };
  });
  return {
    id: idOf(fields),
    kind,
    profile,
    theme: text(fields.get('theme'), MAX_RECORD_TEXT, 'theme'),
    metadata: text(fields.get('metadata'), MAX_RECORD_TEXT, 'metadata'),
    chapters,
    images,
  };
}

function faceOf(value: unknown): FaceKey {
  const fields = fieldsOf(value, ['font', 'style'], 'a face');
  const font = FONT_IDS.find((known) => known === fields.get('font'));
  const style = FONT_STYLES.find((known) => known === fields.get('style'));
  if (font === undefined || style === undefined) throw new ProtocolError('a face must be a curated font and style');
  return { font, style };
}

function runOf(value: unknown, count: { units: number }): LayerRun {
  const fields = fieldsOf(value, ['text', 'x', 'baseline', 'width', 'size', 'face', 'from', 'to'], 'a run');
  const read = text(fields.get('text'), MAX_LAYER_TEXT, 'a run’s text');
  count.units += read.length;
  if (count.units > MAX_LAYER_TEXT) throw new ProtocolError('a page’s layer must hold at most 200 000 units of text');
  const from = whole(fields.get('from'), -1, MAX_CHAPTER_TEXT, 'a run’s start');
  const to = whole(fields.get('to'), -1, MAX_CHAPTER_TEXT, 'a run’s end');
  if (to < from || (from < 0) !== (to < 0)) throw new ProtocolError('a run’s source must be a range, or none');
  return {
    text: read,
    x: finite(fields.get('x'), 'x'),
    baseline: finite(fields.get('baseline'), 'baseline'),
    width: finite(fields.get('width'), 'width'),
    size: finite(fields.get('size'), 'size'),
    face: faceOf(fields.get('face')),
    from,
    to,
  };
}

function blockOf(value: unknown, count: { units: number; runs: number }): LayerBlock {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'image') {
    const fields = fieldsOf(value, ['kind', 'x', 'y', 'width', 'height', 'alt'], 'an image block');
    const alt = text(fields.get('alt'), MAX_LAYER_TEXT, 'alt text');
    count.units += alt.length;
    return { kind, x: finite(fields.get('x'), 'x'), y: finite(fields.get('y'), 'y'), width: finite(fields.get('width'), 'width'), height: finite(fields.get('height'), 'height'), alt };
  }
  if (kind !== 'text') throw new ProtocolError('a block must be text or an image');
  const fields = fieldsOf(value, ['kind', 'runs'], 'a text block');
  const runs = list(fields.get('runs'), MAX_LAYER_RUNS, 'runs');
  count.runs += runs.length;
  if (count.runs > MAX_LAYER_RUNS) throw new ProtocolError('a page’s layer must hold at most 20 000 runs');
  return { kind, runs: runs.map((run) => runOf(run, count)) };
}

/** A message the worker sent, checked; `isBitmap` says whether a page's pixels are a bitmap. Throws {@link ProtocolError}. */
export function parseReaderResponse<B extends object>(value: unknown, isBitmap: (value: unknown) => value is B): ReaderResponse<B> {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  switch (kind) {
    case 'laid': {
      const fields = fieldsOf(value, ['id', 'kind', 'chapters', 'complete'], 'a laid answer');
      const complete = fields.get('complete');
      if (typeof complete !== 'boolean') throw new ProtocolError('complete must be true or false');
      const chapters = list(fields.get('chapters'), MAX_READER_CHAPTERS, 'chapters').map((pages) => whole(pages, 0, MAX_BOOK_PAGES, 'a chapter’s pages'));
      return { id: idOf(fields), kind, chapters, complete };
    }
    case 'drawn': {
      const fields = fieldsOf(value, ['id', 'kind', 'pages'], 'a drawn answer');
      const pages = list(fields.get('pages'), MAX_DRAW_PAGES, 'pages').map((raw): DrawnPage<B> => {
        const page = fieldsOf(raw, ['index', 'chapter', 'bitmap', 'origin', 'layer', 'reading'], 'a drawn page');
        const bitmap = page.get('bitmap');
        if (!isBitmap(bitmap)) throw new ProtocolError('a page’s pixels must be a bitmap');
        const origin = fieldsOf(page.get('origin'), ['x', 'y'], 'an origin');
        const count = { units: 0, runs: 0 };
        const layer = list(page.get('layer'), MAX_LAYER_RUNS, 'a layer').map((block) => blockOf(block, count));
        return {
          index: whole(page.get('index'), 0, MAX_BOOK_PAGES, 'a page'),
          chapter: whole(page.get('chapter'), 0, MAX_READER_CHAPTERS, 'a chapter'),
          bitmap,
          origin: { x: finite(origin.get('x'), 'x'), y: finite(origin.get('y'), 'y') },
          layer,
          reading: text(page.get('reading'), MAX_LAYER_TEXT, 'reading'),
        };
      });
      return { id: idOf(fields), kind, pages };
    }
    case 'stale':
      return { id: idOf(fieldsOf(value, ['id', 'kind'], 'a stale answer')), kind };
    case 'error': {
      const fields = fieldsOf(value, ['id', 'kind', 'message'], 'an error');
      const message = fields.get('message');
      if (typeof message !== 'string') throw new ProtocolError('an error’s message must be text');
      return { id: idOf(fields), kind, message: message.slice(0, MAX_MESSAGE_LENGTH) };
    }
    default:
      throw new ProtocolError('an answer must be laid, drawn, stale or an error');
  }
}
